import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Brush, Eye, Home, Layers, Sparkles } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import SplitSection from "@/components/sections/SplitSection";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/interior-design-hero.jpg";
import introImage from "@/assets/about-lifestyle.jpg";
import beforeAfterImage from "@/assets/bg-interior-1.jpg";
import serviceSellImage from "@/assets/service-sell.jpg";
import servicePrepImage from "@/assets/service-prep.jpg";
import paintingImage from "@/assets/painting-hero.jpg";
import carpentryImage from "@/assets/carpentry-hero.jpg";
import cleaningImage from "@/assets/cleaning-hero.jpg";
import ctaImage from "@/assets/cta-sell.jpg";

const whyDesignMatters = [
  {
    icon: Sparkles,
    title: "Increase perceived value",
    description: "Small, thoughtful changes can increase perceived value.",
  },
  {
    icon: Home,
    title: "Make spaces feel larger and more modern",
    description: "Design choices can make spaces feel larger, cleaner, and more modern.",
  },
  {
    icon: Eye,
    title: "Create emotional connection",
    description: "Strong presentation creates a stronger emotional connection with buyers.",
  },
  {
    icon: Layers,
    title: "Stand out in a competitive market",
    description: "Cohesive design helps your home stand out in a competitive market.",
  },
];

const servicePaths = [
  {
    label: "Path 01",
    eyebrow: "Seller-Focused Path",
    title: "Preparing Your Home for Sale",
    description:
      "When you're selling, presentation can directly affect your final sale price. The goal isn't to over-improve. It's to make the right improvements.",
    image: serviceSellImage,
    imageAlt: "Interior designed to support stronger listing performance",
  },
  {
    label: "Path 02",
    eyebrow: "Lifestyle Path",
    title: "Design for Living",
    description:
      "Your home should work for your life, not against it. Good design does not just look better. It improves how you live every day.",
    image: introImage,
    imageAlt: "Comfortable interior design for everyday living",
  },
];

const designPrepConnectors = [
  {
    eyebrow: "Integrated Service",
    title: "Painting",
    description: "Painting to modernize and refresh spaces.",
    image: paintingImage,
    imageAlt: "Painting services paired with interior design",
    href: "/painting",
    ctaLabel: "Explore Painting",
  },
  {
    eyebrow: "Integrated Service",
    title: "Carpentry",
    description: "Carpentry and repairs to improve condition and eliminate distractions.",
    image: carpentryImage,
    imageAlt: "Carpentry and repairs as part of interior design preparation",
    href: "/carpentry",
    ctaLabel: "Explore Carpentry",
  },
  {
    eyebrow: "Integrated Service",
    title: "Cleaning",
    description: "Professional cleaning to create a polished, move-in-ready feel.",
    image: cleaningImage,
    imageAlt: "Professional cleaning to complete interior design presentation",
    href: "/cleaning",
    ctaLabel: "Explore Cleaning",
  },
];

const benefitsList = [
  "Smarter decisions about where to invest your time and money",
  "Avoiding unnecessary upgrades that don't add value",
  "Creating a space that stands out in the market",
  "A more confident and streamlined process",
];

const integratedApproach = [
  "Real estate expertise",
  "Interior design strategy",
  "Property preparation services",
];

const InteriorDesign = () => {
  useEffect(() => {
    document.title = "Interior Design Services | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Strategic interior design that elevates your home and its value. Hadassah Homes combines design expertise with real estate strategy for maximum impact."
      );
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://hadassahhomesinc.com/interior-design";
  }, []);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Interior Design"
        title="Interior Design with Purpose"
        subtitle="Design That Elevates Your Home and Its Value"
        backgroundImage={heroImage}
      >
        <div className="mx-auto max-w-2xl">
          <p className="text-white text-lg md:text-xl leading-[1.75]">
            Whether you&apos;re preparing to sell or improving your home for your own lifestyle,
            every decision is intentional, practical, and aligned with your goals.
          </p>
        </div>
      </PageHero>

      <ContentSection>
        <SplitSection
          title="Design That Elevates Your Home and Its Value"
          description="Interior design is not just about making a space look better. It is about making it work better."
          image={introImage}
          imageAlt="Interior living room design with refined styling"
        >
          <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-[1.85]">
            <p>
              At Hadassah Homes, we approach design as a strategic tool, one that enhances how
              a space feels, functions, and ultimately performs in the market.
            </p>
            <p>
              Whether you&apos;re preparing to sell or improving your home for your own
              lifestyle, every decision is intentional, practical, and aligned with your goals.
            </p>
          </div>
          <blockquote className="border-l-2 border-accent pl-5 text-foreground font-headline text-xl md:text-2xl leading-[1.45]">
            Ready to elevate your space?
          </blockquote>
          <div className="pt-1">
            <Button variant="gold" size="lg" asChild>
              <Link to="/consultation" className="inline-flex items-center gap-2">
                Book a Consultation
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </SplitSection>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader
          title="Why Design Matters More Than You Think"
          description="The way a home looks and feels directly impacts how it is perceived and what buyers are willing to pay."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {whyDesignMatters.map((item) => (
            <article key={item.title} className="space-y-4 border-t border-accent/35 pt-5">
              <item.icon className="h-6 w-6 text-accent" strokeWidth={1.6} />
              <h3 className="font-headline text-2xl text-secondary-foreground leading-tight">
                {item.title}
              </h3>
              <p className="text-secondary-foreground/84 text-base md:text-lg leading-[1.75]">
                {item.description}
              </p>
            </article>
          ))}
        </div>
        <p className="max-w-3xl mx-auto pt-10 md:pt-12 text-center text-secondary-foreground/82 text-base md:text-lg leading-[1.8]">
          Design is not just visual. It is psychological. And when done right, it influences decisions and outcomes.
        </p>
      </ContentSection>

      <ContentSection>
        <SectionHeader
          title="Real Impact: Before and After Thinking"
          description="The difference between a home that sits and one that stands out often comes down to presentation."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <article className="border border-border bg-card">
            <div className="relative aspect-[16/11] overflow-hidden">
              <img
                src={beforeAfterImage}
                alt="Interior before strategic design updates"
                className="absolute inset-0 h-full w-full object-cover brightness-[0.52] saturate-[0.58] contrast-[0.92]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/52" />
              <span className="absolute left-5 top-5 bg-black/78 px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-white">
                Before
              </span>
            </div>
            <div className="space-y-3 p-6 md:p-8">
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                Dated colors and finishes
              </p>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                Cluttered or inconsistent design
              </p>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                Worn or unmaintained spaces
              </p>
            </div>
          </article>

          <article className="border border-border bg-card">
            <div className="relative aspect-[16/11] overflow-hidden">
              <img
                src={servicePrepImage}
                alt="Interior after strategic design updates"
                className="absolute inset-0 h-full w-full object-cover brightness-[1.22] saturate-[1.24] contrast-[1.08]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/18 via-accent/6 to-transparent" />
              <span className="absolute left-5 top-5 bg-accent px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-accent-foreground">
                After
              </span>
            </div>
            <div className="space-y-3 p-6 md:p-8">
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                Clean, modern, and cohesive look
              </p>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                Bright, inviting, and move-in-ready feel
              </p>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                A space buyers can immediately connect with
              </p>
            </div>
          </article>
        </div>
        <div className="max-w-4xl mx-auto pt-8 md:pt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <p className="text-muted-foreground text-sm md:text-base leading-relaxed md:col-span-1">
            This transformation does not require a full renovation.
          </p>
          <p className="text-muted-foreground text-sm md:text-base leading-relaxed md:col-span-2">
            It requires the right strategy, clean execution, and updates that make your home feel more valuable the moment buyers walk in.
          </p>
        </div>
      </ContentSection>

      <section
        className="mt-12 md:mt-16 py-20 md:py-24"
        style={{ background: "linear-gradient(90deg, #0f3428 0%, #174c3a 50%, #0f3428 100%)" }}
      >
        <div className="mx-auto max-w-wide px-5 md:px-12">
          <div className="mx-auto max-w-3xl text-center space-y-5 md:space-y-6">
            <p className="font-body text-[10px] uppercase tracking-[0.22em] text-accent">
              Design Strategy
            </p>
            <span className="mx-auto block h-px w-20 bg-accent/75" />
            <h2 className="font-headline text-4xl md:text-[3.5rem] text-white leading-[1.08]">
              Design That Drives Results
            </h2>
            <p className="text-white/85 text-base md:text-lg leading-[1.8]">
              We do not design for trends. We design for outcomes. Clean, modern, and
              universally appealing spaces with smart updates that deliver the highest impact.
            </p>
          </div>
        </div>
      </section>

      <ContentSection>
        <SectionHeader
          title="Service Paths Tailored to Your Goal"
          description="Interior design support is structured around where you are now and what result you want next."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {servicePaths.map((path) => (
            <article
              key={path.title}
              className="group relative min-h-[420px] md:min-h-[500px] overflow-hidden border border-border"
            >
              <img
                src={path.image}
                alt={path.imageAlt}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-transparent" />
              <div className="relative z-10 flex min-h-[420px] md:min-h-[500px] items-end p-8 md:p-10">
                <div className="max-w-[30rem] space-y-5">
                  <p className="font-body text-[10px] uppercase tracking-[0.2em] text-white/76">
                    {path.label}
                  </p>
                  <p className="font-body text-[10px] uppercase tracking-[0.22em] text-accent">
                    {path.eyebrow}
                  </p>
                  <h3 className="font-headline text-[2.1rem] md:text-[2.55rem] leading-[1.14] text-white">
                    {path.title}
                  </h3>
                  <p className="text-white/90 text-base md:text-[1.05rem] leading-[1.85] max-w-[27rem]">
                    {path.description}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </ContentSection>

      <section className="bg-[#f7f3ea] py-20 md:py-24">
        <div className="mx-auto max-w-wide px-5 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[0.92fr_1.08fr] gap-12 lg:gap-20 items-start">
            <div className="space-y-7">
              <p className="font-body text-[10px] uppercase tracking-[0.2em] text-accent">
                Strategic System
              </p>
              <h2 className="font-headline text-3xl md:text-5xl text-foreground leading-tight">
                A Fully Integrated Approach
              </h2>
              <div className="space-y-5 text-muted-foreground text-base md:text-lg leading-[1.85] max-w-xl">
                <p className="border-l-2 border-accent pl-5 text-foreground">
                  This is where Hadassah Homes stands apart.
                </p>
                <p>
                  So instead of guessing what matters, or hiring multiple vendors, you get a
                  clear, unified plan.
                </p>
                <p className="text-foreground">
                  Everything works together toward one goal: better results.
                </p>
              </div>
            </div>
            <div className="space-y-0">
                {integratedApproach.map((item, index) => (
                  <div
                    key={item}
                    className={`flex items-start gap-5 md:gap-6 py-6 ${index < integratedApproach.length - 1 ? "border-b border-border" : ""}`}
                  >
                    <p className="font-body text-[11px] uppercase tracking-[0.18em] text-accent min-w-[2.6rem] pt-1">
                      {String(index + 1).padStart(2, "0")}
                    </p>
                    <p className="font-headline text-[1.75rem] md:text-[1.9rem] text-foreground leading-tight max-w-md">
                      {item}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </section>

      <ContentSection>
        <SectionHeader
          title="Design and Property Preparation for Maximum Impact"
          description="Design is most effective when paired with the right execution."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 pt-2">
          {designPrepConnectors.map((service) => (
            <article
              key={service.title}
              className="bg-card border border-border h-full flex flex-col"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={service.image}
                  alt={service.imageAlt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-7 md:p-9 flex h-full flex-col">
                <p className="font-body text-[10px] uppercase tracking-[0.16em] text-accent mb-3">
                  {service.eyebrow}
                </p>
                <h3 className="font-headline text-xl md:text-2xl text-foreground mb-3">
                  {service.title}
                </h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  {service.description}
                </p>
                <Link
                  to={service.href}
                  className="mt-5 inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.12em] text-accent hover:text-accent/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {service.ctaLabel}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </article>
          ))}
        </div>
        <p className="max-w-4xl mx-auto mt-12 md:mt-14 text-center text-muted-foreground text-base md:text-lg leading-[1.85]">
          These elements work together to transform your home from lived in to market-ready.
        </p>
      </ContentSection>

      <section className="bg-[#f7f3ea] py-24 md:py-30">
        <div className="mx-auto max-w-wide px-5 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 md:gap-16 items-start">
          <div className="space-y-7">
            <SectionHeader title="What This Means for You" align="left" />
            <p className="text-muted-foreground text-base md:text-lg leading-[1.85] max-w-xl">
              We focus on what actually makes a difference, not what looks good on paper.
            </p>
          </div>
          <div className="space-y-0 border-l border-accent/32 pl-6 md:pl-8">
            {benefitsList.map((item, index) => (
              <div
                key={item}
                className={`flex items-start gap-3 py-4 md:py-5 ${index < benefitsList.length - 1 ? "border-b border-border" : ""}`}
              >
                <span className="font-body text-xs uppercase tracking-[0.14em] text-accent mt-0.5 shrink-0 min-w-[2.25rem]">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <p className="text-foreground text-base md:text-lg leading-[1.75]">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-24 md:py-32">
        <img
          src={ctaImage}
          alt="Interior design consultation call to action"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-black/38" />
        <div className="relative z-10 mx-auto max-w-wide px-5 md:px-12">
          <div className="mx-auto max-w-2xl text-center space-y-6 md:space-y-7">
            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl leading-tight text-white">
              Let&apos;s Elevate Your Space
            </h2>
            <p className="text-white/90 text-base md:text-lg leading-[1.8]">
              Whether you&apos;re preparing to sell or simply ready to improve your home,
              we&apos;re here to guide you with clarity and intention.
            </p>
            <Button variant="gold" size="lg" className="px-10 md:px-12 py-6 text-base" asChild>
              <Link to="/consultation">Book a Consultation</Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default InteriorDesign;
